import Header from "../components/Header";
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import {
    Alert, Avatar, Box, Breadcrumbs, Button, Card, Chip, InputAdornment, Link, Skeleton, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { MascaraCpf } from "../../utils/MascaraCpf";
import { MascaraTelefone } from "../../utils/MascaraTelefone";
import { MascaraCnpj } from "../../utils/MascaraCnpj";

const corPrimaria = "#0b243d";

// Empresa cadastrada na Spedy (idEmpresaSpedy) e com as configurações do Emissor Nacional salvas (issueType "annfs")
const situacaoCliente = (usuario) => {
    const empresa = usuario?.empresa;
    if (!empresa) return { chave: "semEmpresa", label: "Sem empresa", color: "default" };
    if (empresa.idEmpresaSpedy && empresa.issueType === "annfs") return { chave: "pronto", label: "Pronto para emitir", color: "success" };
    return { chave: "pendente", label: "Integração pendente", color: "warning" };
};

const filtros = [
    { chave: "todos", label: "Todos" },
    { chave: "pronto", label: "Prontos para emitir" },
    { chave: "pendente", label: "Integração pendente" },
    { chave: "semEmpresa", label: "Sem empresa" },
];

// Minúsculas e sem acentos
const normalizar = (texto) => String(texto || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const somenteDigitos = (texto) => String(texto || "").replace(/\D/g, "");

const iniciais = (nome) => String(nome || "?").trim().split(/\s+/).slice(0, 2).map((parte) => parte[0]?.toUpperCase()).join("");

function EmitirNota() {

    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState("");
    const [busca, setBusca] = useState("");
    const [filtro, setFiltro] = useState("todos");
    const [pagina, setPagina] = useState(0);
    const [porPagina, setPorPagina] = useState(10);

    useEffect(() => {
        const getUsuarios = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/listarUsuariosEmitirNota`, {
                    headers: {
                        Authorization: token
                    }
                });
                // Sem clientes o back responde status 400 e não manda a lista
                const lista = response.data.usuarios || [];
                setUsuarios([...lista].sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR")));
                if (response.data.status !== 200 && response.data.status !== 400) {
                    setErro(response.data.erro || "Não foi possível carregar os clientes.");
                }
            } catch (err) {
                console.error(err);
                setErro("Não foi possível carregar os clientes. Tente novamente mais tarde.");
            } finally {
                setCarregando(false);
            }
        };

        getUsuarios();
    }, [token]);

    const contagem = useMemo(() => usuarios.reduce((acc, usuario) => {
        const { chave } = situacaoCliente(usuario);
        acc[chave] = (acc[chave] || 0) + 1;
        return acc;
    }, { todos: usuarios.length }), [usuarios]);

    const usuariosFiltrados = useMemo(() => {
        const termo = normalizar(busca.trim());
        const termoDigitos = somenteDigitos(busca);

        return usuarios.filter((usuario) => {
            if (filtro !== "todos" && situacaoCliente(usuario).chave !== filtro) return false;
            if (!termo) return true;

            const textos = [usuario.nome, usuario.email, usuario.empresa?.razaoSocial, usuario.empresa?.nomeFantasia];
            // CPF, CNPJ e telefone são comparados só pelos dígitos, com ou sem máscara na busca
            const documentos = [usuario.cpf, usuario.empresa?.cnpj, usuario.telefone, String(usuario.idUsuario)];

            return textos.some((campo) => normalizar(campo).includes(termo))
                || (termoDigitos.length > 0 && documentos.some((campo) => somenteDigitos(campo).includes(termoDigitos)));
        });
    }, [usuarios, busca, filtro]);

    const usuariosDaPagina = usuariosFiltrados.slice(pagina * porPagina, pagina * porPagina + porPagina);

    const abrirPerfil = (idUsuario) => navigate(`/EmitirNota/Perfil/${idUsuario}`);

    return (
        <div>
            <Header />
            <Box sx={{ width: "100%", display: "flex", justifyContent: "center", pb: 6 }}>
                <Box sx={{ width: "90%", maxWidth: 1280, mt: 2.5 }}>
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link underline="hover" color="inherit" href="/Dashboard">
                            Dashboard
                        </Link>
                        <Typography sx={{ color: 'text.primary' }}>Emitir Nota</Typography>
                    </Breadcrumbs>

                    <Box sx={{ mt: 2, mb: 3 }}>
                        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Emitir Nota</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Escolha o cliente para emitir notas fiscais e gerenciar o cadastro, a integração e os documentos.
                        </Typography>
                    </Box>

                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        {/* Busca e filtros */}
                        <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                autoFocus
                                placeholder="Buscar por nome, e-mail, CPF, CNPJ, razão social ou telefone"
                                value={busca}
                                onChange={(e) => { setBusca(e.target.value); setPagina(0); }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {filtros.map((item) => {
                                    const ativo = filtro === item.chave;
                                    return (
                                        <Chip
                                            key={item.chave}
                                            label={`${item.label} (${contagem[item.chave] || 0})`}
                                            size="small"
                                            variant={ativo ? "filled" : "outlined"}
                                            onClick={() => { setFiltro(item.chave); setPagina(0); }}
                                            sx={ativo ? { backgroundColor: corPrimaria, color: "#fff", "&:hover": { backgroundColor: "#153d66" } } : undefined}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        {erro && <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>{erro}</Alert>}

                        <TableContainer sx={{ borderTop: 1, borderColor: "divider" }}>
                            <Table size="small" sx={{ minWidth: 820 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#fafafa", "& th": { fontWeight: 600, color: "text.secondary", fontSize: 13, py: 1.5 } }}>
                                        <TableCell>Cliente</TableCell>
                                        <TableCell>CPF</TableCell>
                                        <TableCell>Empresa</TableCell>
                                        <TableCell>Telefone</TableCell>
                                        <TableCell>Situação</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {carregando ? (
                                        Array.from({ length: 5 }).map((_, i) => (
                                            <TableRow key={i}>
                                                {Array.from({ length: 6 }).map((__, j) => (
                                                    <TableCell key={j} sx={{ py: 2 }}><Skeleton /></TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    ) : usuariosDaPagina.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 6, textAlign: "center", borderBottom: 0 }}>
                                                <PersonSearchIcon sx={{ fontSize: 40, color: "text.disabled" }} />
                                                <Typography sx={{ fontWeight: 500, mt: 1 }}>Nenhum cliente encontrado</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {busca || filtro !== "todos" ? "Tente outro termo de busca ou filtro." : "Ainda não há clientes cadastrados."}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        usuariosDaPagina.map((usuario) => {
                                            const situacao = situacaoCliente(usuario);
                                            const empresa = usuario.empresa;
                                            return (
                                                <TableRow
                                                    key={usuario.idUsuario}
                                                    hover
                                                    onClick={() => abrirPerfil(usuario.idUsuario)}
                                                    sx={{ cursor: "pointer", "& td": { py: 1.5 } }}
                                                >
                                                    <TableCell>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                            <Avatar sx={{ width: 36, height: 36, fontSize: 14, backgroundColor: corPrimaria }}>
                                                                {iniciais(usuario.nome)}
                                                            </Avatar>
                                                            <Box sx={{ minWidth: 0 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{usuario.nome || "Sem nome"}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{usuario.email}</Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{usuario.cpf ? MascaraCpf(usuario.cpf) : "—"}</TableCell>
                                                    <TableCell>
                                                        {empresa ? (
                                                            <>
                                                                <Typography variant="body2">{empresa.razaoSocial || "Sem razão social"}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{empresa.cnpj ? MascaraCnpj(empresa.cnpj) : "CNPJ não informado"}</Typography>
                                                            </>
                                                        ) : (
                                                            <Typography variant="body2" color="text.disabled">—</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: "nowrap" }}>{usuario.telefone ? MascaraTelefone(usuario.telefone) : "—"}</TableCell>
                                                    <TableCell>
                                                        <Chip size="small" variant="outlined" color={situacao.color} label={situacao.label} />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            endIcon={<ArrowForwardIcon fontSize="small" />}
                                                            sx={{ textTransform: "none", color: corPrimaria, whiteSpace: "nowrap" }}
                                                            onClick={(e) => { e.stopPropagation(); abrirPerfil(usuario.idUsuario); }}
                                                        >
                                                            Abrir perfil
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {!carregando && usuariosFiltrados.length > 0 && (
                            <TablePagination
                                component="div"
                                count={usuariosFiltrados.length}
                                page={pagina}
                                onPageChange={(e, novaPagina) => setPagina(novaPagina)}
                                rowsPerPage={porPagina}
                                onRowsPerPageChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(0); }}
                                rowsPerPageOptions={[10, 25, 50]}
                                labelRowsPerPage="Por página"
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                                sx={{ borderTop: 1, borderColor: "divider" }}
                            />
                        )}
                    </Card>
                </Box>
            </Box>
        </div>
    );
}

export default EmitirNota;
